import { useEffect, useRef, useState } from "react";
import { Save, Lock, KeyRound, MapPin, User, Camera, Trash2, Mail, Clock } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import ProfileTestimonialCard from "@/components/ProfileTestimonialCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import {
    apiChangePassword, apiUploadAvatar, apiDeleteAvatar,
    apiUpdateReminderPrefs, formatApiError,
} from "@/lib/api";
import { toast } from "sonner";

function initials(name = "") {
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join("") || "?";
}

export default function Profile() {
    const { user, updateProfile, refresh } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [location, setLocation] = useState(user?.location || "");
    const [climateZone, setClimateZone] = useState(user?.climate_zone || "");
    const [tz, setTz] = useState(user?.timezone || "Europe/Dublin");
    const [savingProfile, setSavingProfile] = useState(false);
    const [avatarBusy, setAvatarBusy] = useState(false);
    const [reminderBusy, setReminderBusy] = useState(false);
    const fileInputRef = useRef(null);

    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [pwdBusy, setPwdBusy] = useState(false);

    useEffect(() => {
        if (user) {
            setName(user.name || "");
            setLocation(user.location || "");
            setClimateZone(user.climate_zone || "");
            setTz(user.timezone || "Europe/Dublin");
        }
    }, [user]);

    const saveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await updateProfile({ name, location, climate_zone: climateZone, timezone: tz });
            toast.success("Profile updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingProfile(false);
        }
    };

    const toggleReminder = async (enabled) => {
        setReminderBusy(true);
        try {
            await apiUpdateReminderPrefs(enabled);
            await refresh();
            toast.success(enabled
                ? "We'll send you a Sunday morning digest."
                : "Weekly reminders turned off.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setReminderBusy(false);
        }
    };

    const changePwd = async (e) => {
        e.preventDefault();
        setPwdBusy(true);
        try {
            await apiChangePassword({ current_password: currentPwd, new_password: newPwd });
            toast.success("Password changed");
            setCurrentPwd("");
            setNewPwd("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPwdBusy(false);
        }
    };

    const onAvatarPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAvatarBusy(true);
        try {
            await apiUploadAvatar(file);
            await refresh();
            toast.success("Avatar updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAvatarBusy(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onAvatarRemove = async () => {
        setAvatarBusy(true);
        try {
            await apiDeleteAvatar();
            await refresh();
            toast.success("Avatar removed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setAvatarBusy(false);
        }
    };

    const avatarUrl = user?.avatar_url;

    return (
        <div className="space-y-12" data-testid="profile-page">
            <PageHeader
                overline="Your account"
                title="Profile"
                description="Update your details and password. Location & climate help rooteed tailor advice."
            />

            <section className="journal-card p-6 flex items-center gap-6" data-testid="avatar-section">
                <div className="relative shrink-0">
                    {avatarUrl ? (
                        <img
                            src={avatarUrl}
                            alt="Your avatar"
                            data-testid="profile-avatar-image"
                            className="w-24 h-24 rounded-full object-cover ring-2 ring-[var(--vm-border)]"
                        />
                    ) : (
                        <div
                            data-testid="profile-avatar-initials"
                            className="w-24 h-24 rounded-full bg-[var(--vm-secondary)] text-[var(--vm-bg)] flex items-center justify-center font-serif-display text-3xl ring-2 ring-[var(--vm-border)]"
                        >
                            {initials(user?.name)}
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <Camera className="w-4 h-4 text-[var(--vm-accent)]" />
                        <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Your photo</h3>
                    </div>
                    <p className="text-sm text-[var(--vm-text-muted)]">
                        Square JPG, PNG, WEBP or HEIC up to 5 MB — we auto-crop + resize to 256 px.
                    </p>
                    <div className="flex gap-3 mt-3">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/heic"
                            hidden
                            onChange={onAvatarPick}
                            data-testid="profile-avatar-file-input"
                        />
                        <Button
                            type="button"
                            disabled={avatarBusy}
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="profile-avatar-upload-button"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                        >
                            <Camera className="w-4 h-4 mr-2" />
                            {avatarBusy ? "Uploading…" : avatarUrl ? "Replace" : "Upload photo"}
                        </Button>
                        {avatarUrl && (
                            <Button
                                type="button"
                                variant="outline"
                                disabled={avatarBusy}
                                onClick={onAvatarRemove}
                                data-testid="profile-avatar-remove-button"
                                className="rounded-full border-[var(--vm-border)]"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove
                            </Button>
                        )}
                    </div>
                </div>
            </section>

            <section className="grid md:grid-cols-2 gap-6">
                <form onSubmit={saveProfile} className="journal-card p-6 space-y-4" data-testid="profile-form">
                    <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-[var(--vm-accent)]" />
                        <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Profile details</h3>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Name</Label>
                        <Input
                            data-testid="profile-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Email</Label>
                        <Input
                            disabled value={user?.email || ""}
                            className="mt-1 bg-[var(--vm-bg)] border-[var(--vm-border)] opacity-60"
                            data-testid="profile-email-readonly"
                        />
                        <p className="text-xs text-[var(--vm-text-muted)] mt-1">Email cannot be changed.</p>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Location</Label>
                        <div className="relative mt-1">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vm-text-muted)]" />
                            <Input
                                data-testid="profile-location-input"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Climate zone</Label>
                        <Input
                            data-testid="profile-climate-input"
                            value={climateZone}
                            onChange={(e) => setClimateZone(e.target.value)}
                            className="mt-1 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Timezone</Label>
                        <div className="relative mt-1">
                            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vm-text-muted)]" />
                            <Input
                                data-testid="profile-timezone-input"
                                value={tz}
                                onChange={(e) => setTz(e.target.value)}
                                placeholder="Europe/Dublin"
                                className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                            />
                        </div>
                        <p className="text-xs text-[var(--vm-text-muted)] mt-1">
                            Your IANA timezone (used to time your Sunday digest). Examples: Europe/Dublin, Europe/London, America/New_York.
                        </p>
                    </div>
                    <Button
                        type="submit" disabled={savingProfile}
                        data-testid="profile-save-button"
                        className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {savingProfile ? "Saving…" : "Save changes"}
                    </Button>
                </form>

                <form onSubmit={changePwd} className="journal-card p-6 space-y-4" data-testid="password-form">
                    <div className="flex items-center gap-2 mb-1">
                        <Lock className="w-4 h-4 text-[var(--vm-accent)]" />
                        <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Change password</h3>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">Current password</Label>
                        <Input
                            type="password" required
                            data-testid="profile-current-password-input"
                            value={currentPwd}
                            onChange={(e) => setCurrentPwd(e.target.value)}
                            className="mt-1 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-[var(--vm-sage)]">New password</Label>
                        <Input
                            type="password" required minLength={6}
                            data-testid="profile-new-password-input"
                            value={newPwd}
                            onChange={(e) => setNewPwd(e.target.value)}
                            className="mt-1 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                        />
                    </div>
                    <Button
                        type="submit" disabled={pwdBusy}
                        data-testid="profile-change-password-button"
                        variant="outline"
                        className="rounded-full border-[var(--vm-border)]"
                    >
                        <KeyRound className="w-4 h-4 mr-2" />
                        {pwdBusy ? "Updating…" : "Change password"}
                    </Button>
                </form>
            </section>

            <section className="journal-card p-6 space-y-3" data-testid="reminder-prefs-section">
                <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-[var(--vm-accent)] mt-1 shrink-0" />
                    <div className="flex-1">
                        <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">Sunday garden digest</h3>
                        <p className="text-sm text-[var(--vm-text-muted)] mt-1">
                            A short email every Sunday morning with what to sow, transplant or harvest in the next 7 days,
                            plus any frost or rain warnings — and a single tailored tip from rooteed.
                        </p>
                    </div>
                    <div className="shrink-0 pt-1">
                        <Switch
                            data-testid="reminder-toggle"
                            checked={!!user?.reminder_email_enabled}
                            disabled={reminderBusy}
                            onCheckedChange={toggleReminder}
                        />
                    </div>
                </div>
                <p className="text-xs text-[var(--vm-text-muted)] pl-8">
                    {user?.reminder_email_enabled
                        ? "On — landing in your inbox at 08:00 each Sunday in your timezone."
                        : "Off — flip it on whenever you'd like a weekly nudge."}
                </p>
            </section>

            <ProfileTestimonialCard />
        </div>
    );
}
